import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Search, Download, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Page, Panel, Field } from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import { api, notifyErr, fmtSec, fmtDay } from "../lib/api";
import { useCustomers, useSelectedCustomer } from "../lib/hooks";
import { Tone } from "../lib/labels";

const STATE = { ok: ["Acquisibile", "green"], gia_acquisita: ["Già acquisita", "blue"], duplicato: ["Duplicato", "amber"],
  non_associata: ["Non associata", "red"], ambigua: ["Associazione ambigua", "red"], senza_file: ["File mancante", "red"] };

function StatusPicker({ items, selected, setSelected, all, setAll }) {
  const [q, setQ] = useState("");
  const vis = items.filter((s) => !q || `${s.status} ${s.name}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (code) => setSelected(selected.includes(code) ? selected.filter((x) => x !== code) : [...selected, code]);
  return (
    <div data-testid="status-picker">
      <label className="flex items-center gap-2 p-2 border rounded-sm mb-2 bg-blue-50/50 font-semibold text-xs cursor-pointer">
        <Checkbox checked={all} onCheckedChange={(v) => setAll(!!v)} data-testid="all-statuses-checkbox" />TUTTI GLI ESITI — SENZA ESCLUSIONI
      </label>
      <div className="flex gap-2 mb-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice o descrizione" className="h-8 rounded-sm" data-testid="status-search-input" disabled={all} />
        <Button size="sm" variant="outline" className="h-8 rounded-sm" disabled={all} onClick={() => setSelected([...new Set([...selected, ...vis.map((s) => s.status)])])} data-testid="select-all-statuses-btn">Seleziona tutti</Button>
        <Button size="sm" variant="outline" className="h-8 rounded-sm" disabled={all} onClick={() => setSelected([])} data-testid="deselect-all-statuses-btn">Deseleziona</Button>
      </div>
      <div className={`border rounded-sm max-h-64 overflow-y-auto divide-y ${all ? "opacity-50 pointer-events-none" : ""}`}>
        {vis.map((s) => (
          <label key={s.status} className="flex items-center gap-2 px-2 py-1.5 text-xs row-hover cursor-pointer" data-testid={`status-option-${s.status}`}>
            <Checkbox checked={selected.includes(s.status)} onCheckedChange={() => toggle(s.status)} />
            <span className="font-mono font-medium w-16">{s.status}</span><span className="flex-1 truncate">{s.name || "—"}</span>
            {s.sources.includes("sconosciuto") && <Tone tone="amber">nuovo</Tone>}
            <span className="tabular-nums text-neutral-500 w-10 text-right">{s.count || ""}</span>
          </label>
        ))}
        {!items.length && <div className="p-3 text-xs text-neutral-500">Nessun esito caricato.</div>}
      </div>
      <div className="text-[11px] text-neutral-500 mt-1">{all ? "Tutti gli esiti inclusi, anche codici nuovi o non riconosciuti" : `${selected.length} esiti selezionati`}</div>
    </div>
  );
}

export default function Ricerca() {
  const nav = useNavigate();
  const { customers } = useCustomers();
  const [cid, setCid] = useSelectedCustomer(customers);
  const [cust, setCust] = useState(null);
  const [win, setWin] = useState(null);
  const [pid, setPid] = useState("");
  const [camps, setCamps] = useState([]);
  const [selCamps, setSelCamps] = useState([]);
  const [stats, setStats] = useState([]);
  const [selSt, setSelSt] = useState([]);
  const [allSt, setAllSt] = useState(false);
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const [minDur, setMinDur] = useState(60);
  const [inbound, setInbound] = useState(true);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState([]);
  const [autoAn, setAutoAn] = useState(true);

  useEffect(() => {
    if (!cid) return;
    api.get(`/customers/${cid}`).then((r) => { setCust(r.data); setPid(r.data.predictives_list[0]?.id || ""); });
    api.get(`/customers/${cid}/window`).then((r) => { setWin(r.data); setDFrom(r.data.default); setDTo(r.data.default); });
    api.get(`/customers/${cid}/saved-searches`).then((r) => setSaved(r.data));
    setRes(null);
  }, [cid]);

  const loadMeta = async (refresh = false) => {
    if (!pid) return;
    setBusy("meta");
    try {
      const c = await api.get(`/predictives/${pid}/campaigns?refresh=${refresh}`);
      setCamps(c.data.items);
      const s = await api.get(`/predictives/${pid}/statuses?refresh=${refresh}&campaigns=${selCamps.join(",")}`);
      setStats(s.data.items);
      if (refresh) toast.success("Campagne ed esiti aggiornati da AlFa Dial");
    } catch (e) { notifyErr(e); } finally { setBusy(""); }
  };
  useEffect(() => { setCamps([]); setStats([]); if (pid) loadMeta(false); // eslint-disable-next-line
  }, [pid]);
  useEffect(() => { if (pid) api.get(`/predictives/${pid}/statuses?campaigns=${selCamps.join(",")}`).then((s) => setStats(s.data.items)).catch(() => {}); // eslint-disable-next-line
  }, [selCamps.join(",")]);

  const params = () => ({ campaigns: selCamps, statuses: selSt, all_statuses: allSt, date_from: dFrom, date_to: dTo, min_duration: Number(minDur), include_inbound: inbound });
  const outOfWindow = win && (dFrom < win.min || dTo > win.max || dFrom > dTo);
  const search = async () => {
    setBusy("search");
    try { setRes((await api.post(`/predictives/${pid}/search`, params())).data); } catch (e) { notifyErr(e); } finally { setBusy(""); }
  };
  const acquire = async () => {
    setBusy("acq");
    try {
      const j = (await api.post(`/predictives/${pid}/acquire`, { ...params(), auto_analyze: autoAn })).data;
      toast.success(`Job di acquisizione creato: ${j.total} registrazioni`);
      nav("/elaborazioni");
    } catch (e) { notifyErr(e); } finally { setBusy(""); }
  };
  const saveSearch = async () => {
    const name = window.prompt("Nome della ricerca salvata");
    if (!name) return;
    await api.post(`/customers/${cid}/saved-searches`, { name, predictive_id: pid, params: { campaigns: selCamps, statuses: selSt, all_statuses: allSt, min_duration: Number(minDur), include_inbound: inbound } });
    setSaved((await api.get(`/customers/${cid}/saved-searches`)).data);
    toast.success("Ricerca salvata");
  };
  const applySaved = (id) => {
    const s = saved.find((x) => x.id === id);
    if (!s) return;
    if (s.predictive_id) setPid(s.predictive_id);
    setSelCamps(s.params.campaigns || []); setSelSt(s.params.statuses || []); setAllSt(!!s.params.all_statuses);
    setMinDur(s.params.min_duration ?? 60); setInbound(s.params.include_inbound ?? true);
  };
  const rows = useMemo(() => res?.rows || [], [res]);

  return (
    <Page title="Ricerca e Acquisizione" subtitle="Ricerca metadati da AlFa Dial (sola lettura), separata dal download dell'audio. Finestra consentita: ultimi 5 giorni nel fuso del cliente." testid="ricerca-page"
      actions={<CustomerSelect customers={customers} value={cid} onChange={setCid} />}>
      {cust && !cust.predictives_list.length ? (
        <Panel><p className="text-sm">Nessun predictive configurato per questo cliente. Configurarlo in <b>Clienti e Predictive</b> oppure usare il <b>Caricamento manuale</b>.</p></Panel>
      ) : (
      <div className="grid xl:grid-cols-[420px_1fr] gap-4">
        <Panel title="Filtri" actions={<Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => loadMeta(true)} disabled={busy === "meta"} data-testid="refresh-statuses-btn"><RefreshCw size={13} className={`mr-1 ${busy === "meta" ? "animate-spin" : ""}`} />Aggiorna esiti</Button>}>
          <div className="space-y-3">
            <Field label="Predictive">
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger className="h-8 rounded-sm" data-testid="predictive-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{(cust?.predictives_list || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {saved.length > 0 && <Field label="Ricerche salvate">
              <Select onValueChange={applySaved}><SelectTrigger className="h-8 rounded-sm" data-testid="saved-search-select"><SelectValue placeholder="Applica ricerca salvata" /></SelectTrigger>
                <SelectContent>{saved.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
            </Field>}
            <div className="grid grid-cols-2 gap-2">
              <Field label="Dal"><Input type="date" min={win?.min} max={win?.max} value={dFrom} onChange={(e) => setDFrom(e.target.value)} className="h-8 rounded-sm" data-testid="date-from-input" /></Field>
              <Field label="Al"><Input type="date" min={win?.min} max={win?.max} value={dTo} onChange={(e) => setDTo(e.target.value)} className="h-8 rounded-sm" data-testid="date-to-input" /></Field>
            </div>
            {win && <p className={`text-[11px] ${outOfWindow ? "text-red-700" : "text-neutral-500"}`} data-testid="window-info">Consentito dal {fmtDay(win.min)} al {fmtDay(win.max)} ({win.timezone}). Predefinito: ieri ({fmtDay(win.default)}).</p>}
            <div className="grid grid-cols-2 gap-2 items-end">
              <Field label="Durata minima (s, strettamente maggiore)"><Input type="number" min={0} value={minDur} onChange={(e) => setMinDur(e.target.value)} className="h-8 rounded-sm" data-testid="min-duration-input" /></Field>
              <label className="flex items-center gap-2 text-xs pb-2"><Switch checked={inbound} onCheckedChange={setInbound} data-testid="inbound-switch" />Includi inbound</label>
            </div>
            <Field label={`Campagne (${selCamps.length ? selCamps.length + " selezionate" : "tutte"})`}>
              <div className="border rounded-sm max-h-36 overflow-y-auto divide-y" data-testid="campaign-list">
                {camps.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1 text-xs row-hover cursor-pointer">
                    <Checkbox checked={selCamps.includes(c.id)} onCheckedChange={() => setSelCamps(selCamps.includes(c.id) ? selCamps.filter((x) => x !== c.id) : [...selCamps, c.id])} data-testid={`campaign-option-${c.id}`} />
                    <span className="font-mono">{c.id}</span><span className="truncate flex-1 text-neutral-600">{c.name}</span><span className="text-neutral-400">{c.type}</span>
                  </label>
                ))}
                {!camps.length && <div className="p-2 text-xs text-neutral-500">{busy === "meta" ? "Caricamento da AlFa Dial…" : "Nessuna campagna (verificare la connessione)"}</div>}
              </div>
            </Field>
            <StatusPicker items={stats} selected={selSt} setSelected={setSelSt} all={allSt} setAll={setAllSt} />
            <div className="flex gap-2">
              <Button className="flex-1 rounded-sm bg-[#0047FF] h-9" onClick={search} disabled={!pid || busy === "search" || outOfWindow} data-testid="search-recordings-btn"><Search size={14} className="mr-1" />{busy === "search" ? "Ricerca…" : "Cerca registrazioni"}</Button>
              <Button variant="outline" className="rounded-sm h-9" onClick={saveSearch} disabled={!pid} data-testid="save-search-btn"><Save size={14} /></Button>
            </div>
          </div>
        </Panel>
        <div className="space-y-4 min-w-0">
          {res ? <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white border rounded-sm p-3"><div className="eyebrow">Trovate</div><div className="text-2xl font-head font-semibold" data-testid="search-total">{res.total}</div></div>
              <div className="bg-white border rounded-sm p-3"><div className="eyebrow">Acquisibili</div><div className="text-2xl font-head font-semibold text-[#0047FF]" data-testid="search-acquirable">{res.acquirable}</div></div>
              <div className="bg-white border rounded-sm p-3"><div className="eyebrow">Audio totale</div><div className="text-2xl font-head font-semibold">{Math.round(res.total_seconds / 60)} min</div></div>
              <div className="bg-white border rounded-sm p-3 text-xs space-y-0.5" data-testid="search-states">{Object.entries(res.states).map(([k, v]) => <div key={k}>{STATE[k]?.[0] || k}: <b>{v}</b></div>)}</div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Panel title="Per campagna"><div className="text-xs space-y-1" data-testid="by-campaign">{Object.entries(res.by_campaign).map(([k, v]) => <div key={k} className="flex justify-between"><span className="font-mono">{k}</span><b>{v}</b></div>)}</div></Panel>
              <Panel title="Per esito"><div className="text-xs space-y-1" data-testid="by-status">{Object.entries(res.by_status).map(([k, v]) => <div key={k} className="flex justify-between"><span className="font-mono">{k}</span><b>{v}</b></div>)}</div></Panel>
            </div>
            <Panel title="Registrazioni" actions={<>
              <label className="flex items-center gap-2 text-xs"><Switch checked={autoAn} onCheckedChange={setAutoAn} data-testid="auto-analyze-switch" />Avvia analisi AI dopo l'acquisizione</label>
              <Button size="sm" className="h-7 rounded-sm bg-[#0047FF]" onClick={acquire} disabled={!res.acquirable || busy === "acq"} data-testid="acquire-recordings-btn"><Download size={13} className="mr-1" />Acquisisci registrazioni ({res.acquirable})</Button>
            </>}>
              <div className="overflow-x-auto max-h-[520px]">
                <Table>
                  <TableHeader><TableRow><TableHead>Stato</TableHead><TableHead>ID reg.</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Durata</TableHead><TableHead>Telefono</TableHead><TableHead>Lead</TableHead><TableHead>Agente</TableHead><TableHead>Campagna</TableHead><TableHead>Esito</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.recording_id} className="row-hover text-xs" data-testid={`search-row-${r.recording_id}`}>
                        <TableCell><Tone tone={STATE[r.state]?.[1]}>{STATE[r.state]?.[0] || r.state}</Tone></TableCell>
                        <TableCell className="font-mono">{r.recording_id}</TableCell><TableCell className="whitespace-nowrap">{r.call_date}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtSec(r.duration)}</TableCell><TableCell className="font-mono">{r.phone || "—"}</TableCell>
                        <TableCell className="font-mono">{r.lead_id}</TableCell><TableCell>{r.agent}</TableCell><TableCell className="font-mono">{r.campaign}</TableCell>
                        <TableCell className="font-mono">{r.status}</TableCell><TableCell className="text-neutral-500">{r.notes.join("; ")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {res.truncated && <p className="text-xs text-neutral-500 p-2">Visualizzate le prime 5000 righe.</p>}
              </div>
            </Panel>
          </> : <Panel><p className="text-sm text-neutral-500">Impostare i filtri e premere <b>Cerca registrazioni</b>. Nessun audio viene scaricato in questa fase.</p></Panel>}
        </div>
      </div>)}
    </Page>
  );
}
